import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { searchApi } from '@/utils/api';
import type { SearchResult, SearchFilters } from '@/types/search';

interface GlobalSearchResult {
  id: string;
  entityType: string;
  title: string;
  subtitle?: string;
  highlights: Record<string, string[]>;
  score: number;
  metadata: Record<string, any>;
}

interface GlobalSearchResponse {
  query: string;
  totalCount: number;
  results: GlobalSearchResult[];
  facets: Record<string, number>;
  executionTime: number;
}

interface SearchState {
  query: string;
  results: GlobalSearchResult[];
  facets: Record<string, number>;
  totalCount: number;
  executionTime: number;
  selectedTypes: string[];
  isLoading: boolean;
  error: string | null;
  isDropdownOpen: boolean;
}

const initialState: SearchState = {
  query: '',
  results: [],
  facets: {},
  totalCount: 0,
  executionTime: 0,
  selectedTypes: [],
  isLoading: false,
  error: null,
  isDropdownOpen: false,
};

// Async thunk for global search
export const globalSearch = createAsyncThunk<
  GlobalSearchResponse,
  { query: string; types?: string[]; limit?: number },
  { rejectValue: string }
>('search/globalSearch', async ({ query, types, limit = 20 }, { rejectWithValue }) => {
  try {
    const response = await searchApi.globalSearch({
      query,
      entityTypes: types,
      limit,
      includeHighlights: true,
    }) as { success: boolean; data: GlobalSearchResponse };
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error?.message || 'Search failed');
  }
});

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setSelectedTypes: (state, action: PayloadAction<string[]>) => {
      state.selectedTypes = action.payload;
    },
    clearSearch: (state) => {
      state.query = '';
      state.results = [];
      state.facets = {};
      state.totalCount = 0;
      state.executionTime = 0;
      state.error = null;
      state.isDropdownOpen = false;
    },
    setDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.isDropdownOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(globalSearch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(globalSearch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.results = action.payload.results;
        state.facets = action.payload.facets;
        state.totalCount = action.payload.totalCount;
        state.executionTime = action.payload.executionTime;
        state.isDropdownOpen = true;
      })
      .addCase(globalSearch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Search failed';
      });
  },
});

export const { setQuery, setSelectedTypes, clearSearch, setDropdownOpen } = searchSlice.actions;

// Selectors
export const selectSearchResults = (state: { search: SearchState }) => state.search.results;
export const selectSearchFacets = (state: { search: SearchState }) => state.search.facets;
export const selectSearchQuery = (state: { search: SearchState }) => state.search.query;
export const selectSearchLoading = (state: { search: SearchState }) => state.search.isLoading;
export const selectSearchError = (state: { search: SearchState }) => state.search.error;
export const selectIsDropdownOpen = (state: { search: SearchState }) => state.search.isDropdownOpen;

export default searchSlice.reducer;
